/**
 * เขตเวลาของระบบ — `Asia/Bangkok` (D-055)
 *
 * ที่นี่คือ **ที่เดียว** ที่ระบบเขียน offset ของเวลาเชิงธุรกิจ ก่อนหน้านี้มีสองที่ที่
 * ทำเรื่องเดียวกันด้วยกฎตรงข้ามกัน: `desktop/bom/bomView.ts` ตรึงไว้ที่ `+07:00`
 * ตาม D-055 ส่วน `lib/productionOrders/dates.ts` อ่านจาก `getTimezoneOffset()`
 * ของเครื่องผู้ใช้ พร้อมคอมเมนต์ที่ให้เหตุผลว่าค่าคงที่ "จะผิดทันทีที่มีใครเปิด
 * หน้าจอจากอีกโซนเวลา" — ซึ่งเป็นเหตุผลที่ฟังดูดีและ **กลับด้านกับสิ่งที่ D-055
 * ตัดสินไว้**
 *
 * D-055 ตัดสินอย่างนั้นเพราะ "วันที่มีผล" เป็นข้อเท็จจริงทางธุรกิจ ไม่ใช่เวลาบนนาฬิกา
 * ของผู้ใช้: วิศวกรที่เปิดโน้ตบุ๊กซึ่งตั้งเขตเวลาอื่นแล้วเลือก "1 ต.ค." ต้องได้
 * **จุดเวลาเดียวกัน** กับเพื่อนที่นั่งอยู่ในโรงงาน ไม่งั้นกฎอย่าง
 * `EFFECTIVE_DATE_NOT_AFTER_CURRENT` จะให้ผลต่างกันสำหรับวันเดียวกันโดยไม่มีใคร
 * สืบได้ว่าทำไม
 *
 * ไทยไม่มีและไม่เคยมี DST ค่าคงที่นี้จึงถูกตลอดปี ถ้าวันหนึ่ง CAP มีโรงงานในเขตเวลา
 * อื่น เขตเวลาต้องผูกกับ **Plant** ไม่ใช่กลับไปผูกกับเครื่องผู้ใช้ — และตรงนี้คือจุด
 * เดียวที่ต้องแก้
 */
export const BUSINESS_UTC_OFFSET = '+07:00';

/** `YYYY-MM-DD` เป็นวันที่ที่มีอยู่จริงหรือไม่ — ตรวจผ่าน UTC จึงไม่ขึ้นกับเครื่อง */
export function isRealCalendarDay(day: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    return false;
  }
  const probe = new Date(`${day}T00:00:00Z`);
  return !Number.isNaN(probe.getTime()) && probe.toISOString().slice(0, 10) === day;
}

/**
 * ค่าจากช่อง `<input type="date">` (`YYYY-MM-DD`) → timestamp เชิงธุรกิจ
 *
 * คืน `null` เมื่อไม่ใช่วันที่จริง — ผู้เรียกต้องปฏิเสธก่อนส่ง ไม่ใช่ส่งค่าเพี้ยนออกไป
 */
export function businessDayToIso(day: string): string | null {
  return isRealCalendarDay(day) ? `${day}T00:00:00${BUSINESS_UTC_OFFSET}` : null;
}

/**
 * ค่าจากช่อง `<input type="datetime-local">` → timestamp เชิงธุรกิจ
 *
 * เวลาที่ผู้ใช้พิมพ์ถือเป็นเวลาของโรงงาน ไม่ใช่เวลาของเครื่องที่พิมพ์ — ตาม D-055
 *
 * รับทั้ง `YYYY-MM-DDTHH:mm` และ `YYYY-MM-DDTHH:mm:ss` แล้ว **เก็บวินาทีที่ผู้ใช้
 * พิมพ์มาไว้ตามเดิม** ช่อง `datetime-local` ส่งวินาทีมาได้เมื่อมี `step` และการ
 * ตัดทิ้งคือการเปลี่ยนค่าที่ผู้ใช้กรอกโดยไม่มีใครขอ
 *
 * คืน `null` เมื่อรูปแบบไม่ถูกหรือวันที่ไม่มีอยู่จริง
 */
export function businessMinuteToIso(value: string): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (match === null || !isRealCalendarDay(match[1])) {
    return null;
  }
  const [, day, hour, minute, second] = match;
  if (Number(hour) > 23 || Number(minute) > 59 || Number(second ?? '0') > 59) {
    return null;
  }
  return `${day}T${hour}:${minute}:${second ?? '00'}${BUSINESS_UTC_OFFSET}`;
}
